import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { signOut } from 'aws-amplify/auth'
import { LayoutDashboard, Users, CreditCard, Activity, Cpu, LogOut, Settings, Moon, Sun, UserCog, Key } from 'lucide-react'
import OrgSelector from './OrgSelector'
import { HealthIndicator } from './HealthIndicator'
import ChangePasswordModal from './ChangePasswordModal'
import { useOrg } from '../contexts/OrgContext'
import { useTheme } from '../contexts/ThemeContext'
import './Layout.css'

function Layout({ user, onSignOut, children }) {
  const location = useLocation()
  const { currentOrg, isSuperAdmin, loading, userProfile } = useOrg()
  const { isDark, toggleTheme } = useTheme()
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Generate initials from first and last name
  const getInitials = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name[0]}${userProfile.last_name[0]}`.toUpperCase()
    }
    return (user?.signInDetails?.loginId?.[0] || 'U').toUpperCase()
  }

  // Get display name
  const getDisplayName = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name} ${userProfile.last_name}`
    }
    return user?.signInDetails?.loginId?.split('@')[0] || 'User'
  }

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Clients', path: '/clients', icon: Users },
    { name: 'Cards', path: '/cards', icon: CreditCard },
    { name: 'Events', path: '/events', icon: Activity },
    { name: 'Devices', path: '/devices', icon: Cpu },
    ...(isSuperAdmin ? [{ name: 'Users', path: '/users', icon: UserCog }] : []),
    ...(isSuperAdmin ? [{ name: 'System Admin', path: '/system-admin', icon: Settings }] : [])
  ]

  async function handleSignOut() {
    try {
      await signOut()
      onSignOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  function isActive(path) {
    return location.pathname === path
  }

  return (
    <div className="layout">
      {import.meta.env.VITE_ENVIRONMENT === 'development' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(90deg, #f59e0b, #f97316)',
          color: '#000',
          padding: '0.5rem',
          textAlign: 'center',
          fontWeight: 'bold',
          zIndex: 9999,
          fontSize: '0.875rem'
        }}>
          ⚠️ DEVELOPMENT ENVIRONMENT - Changes affect production data
        </div>
      )}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="sidebar-title">
            {loading || !currentOrg ? 'Powered by JAGTEK' : currentOrg.organization?.name}
          </h1>
          <p className="sidebar-subtitle">Access Control</p>
        </div>

        <nav className="sidebar-nav">
          {navigation.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </Link>
            )
          })}
        </nav>

        <div className="sidebar-footer">
          <div style={{ marginBottom: '1rem' }}>
            <OrgSelector />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <HealthIndicator />
          </div>

          <div className="user-info">
            <div className="user-avatar">
              {getInitials()}
            </div>
            <div className="user-details">
              <div className="user-name">{getDisplayName()}</div>
              <div className="user-email">{user?.signInDetails?.loginId || ''}</div>
            </div>
          </div>

          {successMessage && (
            <div style={{
              padding: '0.5rem',
              backgroundColor: '#d1fae5',
              color: '#065f46',
              borderRadius: '0.375rem',
              marginBottom: '0.5rem',
              fontSize: '0.75rem',
              textAlign: 'center'
            }}>
              {successMessage}
            </div>
          )}

          <button
            className="btn-signout"
            onClick={() => setShowChangePasswordModal(true)}
            style={{ marginBottom: '0.5rem' }}
          >
            <Key size={18} />
            <span>Change Password</span>
          </button>
          <button
            className="btn-signout"
            onClick={toggleTheme}
            style={{ marginBottom: '0.5rem' }}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
            <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          <button className="btn-signout" onClick={handleSignOut}>
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
          <div style={{
            marginTop: '1rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            fontSize: '0.75rem',
            color: '#64748b',
            textAlign: 'center'
          }}>
            v1.6.0
          </div>
        </div>
      </aside>

      <main className="main-content">
        <div className="content-wrapper">
          {children}
        </div>
        <footer style={{
          padding: '1rem 2rem',
          borderTop: '1px solid var(--border)',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--text-light)',
          marginTop: 'auto'
        }}>
          <p style={{ margin: 0 }}>
            © {new Date().getFullYear()} JAGTEK. All rights reserved.
          </p>
        </footer>
      </main>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSuccess={(message) => {
          setSuccessMessage(message)
          setTimeout(() => setSuccessMessage(''), 3000)
        }}
      />
    </div>
  )
}

export default Layout
